/**
 * SaveContactDialog — reusable modal to save a phone number as a new
 * candidate. Used from:
 *   - DialerFAB (bouton "Enregistrer comme candidat" à côté du bouton "Appeler")
 *   - CallCenter (bouton "Enregistrer comme candidat" sur chaque ligne
 *     d'appel où candidate_id est null — iter94, CEO 2026-09-01)
 *
 * Props:
 *   - open, onOpenChange: standard Radix dialog controls
 *   - phone: E.164 phone the user is saving as a candidate
 *   - callLogId: optional. Si fourni, le candidat créé est
 *     automatiquement lié à ce CallLog (via `POST /api/twilio/calls/{id}/link-candidate`).
 *   - onSaved(cand): callback after successful creation.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { Check, FileText, Loader2, UserPlus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";


export default function SaveContactDialog({ open, onOpenChange, phone, callLogId = null, onSaved }) {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [note, setNote] = useState("");
    const [layanOfferId, setLayanOfferId] = useState("");
    const [cvFile, setCvFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [offers, setOffers] = useState([]);

    // Reset form + load Publiée offers when dialog opens
    useEffect(() => {
        if (!open) return;
        setFirstName(""); setLastName(""); setEmail(""); setNote(""); setLayanOfferId(""); setCvFile(null);
        api.get("/integrations/layan/offers").then(({ data }) => {
            const publiees = (data || []).filter((o) => o.status === "Publiée");
            setOffers(publiees);
        }).catch(() => setOffers([]));
    }, [open]);

    const canSave = firstName.trim() && lastName.trim() && phone;

    const submit = async () => {
        if (!canSave) return;
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("phone", phone);
            fd.append("first_name", firstName.trim());
            fd.append("last_name", lastName.trim());
            if (email.trim()) fd.append("email", email.trim());
            if (note.trim()) fd.append("note", note.trim());
            if (layanOfferId) fd.append("layan_offer_id", String(layanOfferId));
            if (cvFile) fd.append("cv", cvFile);
            if (callLogId) fd.append("call_log_id", String(callLogId));
            const { data } = await api.post("/candidates/manual-from-call", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onSaved && onSaved(data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const status = e?.response?.status;
            if (typeof detail === "object" && detail?.code === "duplicate_contact") {
                toast.error(detail.message || "Un candidat existe déjà avec ce numéro/email");
            } else if (typeof detail === "string") {
                toast.error(detail);
            } else if (status === 429) {
                toast.error("Trop de requêtes — patientez quelques secondes puis réessayez.");
            } else if (status >= 500) {
                toast.error(`Serveur momentanément indisponible (${status}). Réessayez dans un instant.`);
            } else if (e?.message?.includes("Network")) {
                toast.error("Connexion réseau perdue — vérifiez votre connexion et réessayez.");
            } else {
                toast.error(`Impossible d'enregistrer le contact${status ? ` (HTTP ${status})` : ""}`);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[80] bg-shariti-black/60 backdrop-blur-sm" />
                <Dialog.Content
                    data-testid="save-contact-dialog"
                    className="fixed left-1/2 top-1/2 z-[81] w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-white shadow-2xl focus:outline-none max-h-[90vh] overflow-y-auto"
                >
                    <div className="p-5">
                        <div className="flex items-center justify-between mb-4">
                            <Dialog.Title className="text-base font-extrabold text-shariti-black inline-flex items-center gap-2">
                                <UserPlus className="h-4 w-4 text-shariti-orange" />
                                Enregistrer comme candidat
                            </Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="h-8 w-8 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500">
                                    <X className="h-4 w-4" />
                                </button>
                            </Dialog.Close>
                        </div>

                        {/* Phone summary */}
                        <div className="rounded-2xl bg-emerald-50 border-2 border-emerald-200 px-4 py-2 mb-4">
                            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                                {callLogId ? "Numéro appelé" : "Numéro composé"}
                            </div>
                            <div className="text-lg font-black text-emerald-800" data-testid="save-contact-phone">{phone}</div>
                            {callLogId && (
                                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 mt-0.5">
                                    Le candidat sera lié à cet appel
                                </div>
                            )}
                        </div>

                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Prénom *</label>
                                    <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                                        data-testid="save-contact-firstname"
                                        className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-semibold focus:border-shariti-orange focus:outline-none" />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Nom *</label>
                                    <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
                                        data-testid="save-contact-lastname"
                                        className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-semibold focus:border-shariti-orange focus:outline-none" />
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Email</label>
                                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                    placeholder="candidat@example.com"
                                    data-testid="save-contact-email"
                                    className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-semibold focus:border-shariti-orange focus:outline-none" />
                            </div>
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Offre à associer</label>
                                <select value={layanOfferId} onChange={(e) => setLayanOfferId(e.target.value)}
                                    data-testid="save-contact-offer"
                                    className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-semibold focus:border-shariti-orange focus:outline-none bg-white">
                                    <option value="">Aucune offre spécifique</option>
                                    {offers.map((o) => (
                                        <option key={o.layan_offer_id} value={o.layan_offer_id}>
                                            {o.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">CV (PDF)</label>
                                <label className="w-full rounded-xl border-2 border-dashed border-slate-300 hover:border-shariti-orange transition px-3 py-3 text-sm font-semibold text-slate-500 flex items-center justify-center gap-2 cursor-pointer">
                                    <FileText className="h-4 w-4" />
                                    <span>{cvFile ? cvFile.name : "Choisir un PDF (optionnel)"}</span>
                                    <input type="file" accept=".pdf,application/pdf"
                                        data-testid="save-contact-cv"
                                        onChange={(e) => setCvFile(e.target.files?.[0] || null)} className="hidden" />
                                    {cvFile && <Check className="h-4 w-4 text-emerald-500" />}
                                </label>
                            </div>
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">Note interne</label>
                                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                                    placeholder="Contexte de l'appel, source, etc."
                                    data-testid="save-contact-note"
                                    className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-semibold focus:border-shariti-orange focus:outline-none resize-none" />
                            </div>
                        </div>

                        <div className="mt-5 flex gap-2">
                            <button type="button" onClick={() => onOpenChange(false)}
                                className="flex-1 h-11 rounded-xl border-2 border-slate-200 hover:bg-slate-50 font-black text-sm">
                                Annuler
                            </button>
                            <button type="button" onClick={submit} disabled={!canSave || saving}
                                data-testid="save-contact-submit"
                                className="flex-1 h-11 rounded-xl bg-shariti-orange hover:bg-shariti-orange-dark text-white font-black text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                                {saving ? "Enregistrement…" : "Enregistrer"}
                            </button>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
